import { ClubRole, GlobalRole, TUserData } from "./common/types/user";
import { decodeJwt } from "./common/utils/jwt";
import { resolvePostLoginRoute } from "./common/utils/post-login-route";
import { SessionAuthCookies } from "./libs/cookies";
import { registerMiddleware } from "./libs/react-router";

function getAuthUser(): TUserData | null {
  const token = SessionAuthCookies.get();
  if (!token) return null;
  try {
    const decoded = decodeJwt<TUserData>(token);
    if (decoded.exp && decoded.exp * 1000 <= Date.now()) return null;
    return decoded;
  } catch {
    return null;
  }
}

function isAuthenticated(): boolean {
  return getAuthUser() !== null;
}

const publicRoutes = [
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/magic-link",
  "/match",
  "/clip",
  "/op",
  "/stream",
];
const publicPrefixes = ["/match/", "/clip/", "/op/", "/stream/", "/p/"];

function isPublicRoute(pathname: string): boolean {
  if (publicRoutes.includes(pathname)) return true;
  return publicPrefixes.some((prefix) => pathname.startsWith(prefix));
}

// Admin-only route prefixes (master data, settings)
const adminOnlyPrefixes = [
  "/admin/sport",
  "/admin/competition",
  "/admin/season",
  "/admin/role-type",
  "/admin/country",
  "/admin/region",
  "/admin/city",
  "/admin/registration",
  "/admin/config",
];

// Routes accessible by analysts (and coaches and admins)
const analystPrefixes = [
  "/admin/tag",
];

// Routes accessible by club staff — coach, manager, or admin (mirrors the
// API's `Authorization.isClubStaff`) — not analyst, not player. `manager` is
// a sibling role (PRD §4 / D3), not a rung above analyst, but D3 only covers
// Team Operations capabilities (training sessions, groups, squads, cards);
// it says nothing about club/team/player administration, so widening these
// three to manager does not contradict it. `/admin/team-event` is the first
// Team Operations MVP screen here (P1-7): coach + manager + admin per that
// task's acceptance criteria.
const clubStaffPrefixes = [
  "/admin/club",
  "/admin/team",
  "/admin/player",
  "/admin/team-event",
  // Team Workspace FE-03 — manager's daily operational/roster screens
  // (team-management/SPEC.md §3). Placeholder pages today; coach + manager
  // + admin per the task's acceptance criteria.
  "/admin/broadcasts",
  "/admin/attendance",
  "/admin/players-contacts",
  "/admin/programme-logistics",
];

// Coach-only subset of club staff — excludes manager. These are the coach's
// private review-delivery and operational-input screens (coach-review/
// SPEC.md §2); a manager broadcasting operational updates has no business
// reading a coach's sent reviews or the coach-facing availability view.
const coachOnlyPrefixes = [
  "/admin/sent-reviews",
  "/admin/team-availability",
];

function matchesPrefix(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(prefix + "/"));
}

/**
 * Pure route-guard decision, split out from the registered handler so it can
 * be unit tested without mocking cookies/JWT decoding.
 *
 * Returns the redirect path if `pathname` is restricted and `clubRole` (plus
 * `globalRole`) doesn't clear it, otherwise `undefined`.
 */
export function resolveRestrictedRouteRedirect(
  pathname: string,
  globalRole: GlobalRole,
  clubRole: ClubRole | null
): string | undefined {
  if (globalRole === "admin") return undefined;

  if (matchesPrefix(pathname, adminOnlyPrefixes)) {
    return "/admin";
  }

  if (
    matchesPrefix(pathname, clubStaffPrefixes) &&
    clubRole !== "coach" &&
    clubRole !== "manager"
  ) {
    return "/admin";
  }

  if (matchesPrefix(pathname, coachOnlyPrefixes) && clubRole !== "coach") {
    return "/admin";
  }

  if (
    matchesPrefix(pathname, analystPrefixes) &&
    clubRole !== "coach" &&
    clubRole !== "analyst"
  ) {
    return "/admin";
  }

  return undefined;
}

registerMiddleware({
  matcher: "^/$",
  handler: () => {
    if (isAuthenticated()) {
      return { redirect: "/admin" };
    }
    // "/" is public - allow unauthenticated users to view it
    return;
  },
});

registerMiddleware({
  matcher: ".*",
  handler: (request) => {
    const url = new URL(request.url);
    const pathname = url.pathname;

    if (pathname === "/") return;

    const authed = isAuthenticated();
    const isPublic = isPublicRoute(pathname);

    if (!isPublic && !authed) {
      return { redirect: `/login?redirect=${encodeURIComponent(pathname)}` };
    }

    if (isPublic && authed && publicRoutes.includes(pathname)) {
      return { redirect: "/admin" };
    }

    // A JWT issued before the "owner" -> "coach" club_role rename may still
    // carry the old claim. Treat it as a stale session (force re-login)
    // instead of silently dropping the user's coach permissions.
    if (authed && !isPublic) {
      const user = getAuthUser();
      if (user && (user.club_role as string) === "owner") {
        SessionAuthCookies.remove();
        localStorage.removeItem("refresh_token");
        return { redirect: `/login?redirect=${encodeURIComponent(pathname)}` };
      }
    }

    // Player accounts live under /player and should not access staff /admin,
    // and are exempt from the staff/coach club-onboarding flow below.
    if (authed && !isPublic) {
      const user = getAuthUser();
      if (user?.account_type === "player") {
        if (pathname === "/admin" || pathname.startsWith("/admin/")) {
          return { redirect: "/player/dashboard" };
        }
      } else if (user) {
        // Mandatory club onboarding for club-less non-admin users
        const needsOnboarding = user.global_role !== "admin" && !user.club_id;

        if (needsOnboarding && pathname !== "/onboarding") {
          return { redirect: "/onboarding" };
        }

        if (pathname === "/onboarding" && !needsOnboarding) {
          return { redirect: resolvePostLoginRoute(user) };
        }
      }
    }

    // Block restricted routes for non-admin users
    if (authed && !isPublic) {
      const user = getAuthUser();
      if (user) {
        const redirect = resolveRestrictedRouteRedirect(
          pathname,
          user.global_role,
          user.club_role
        );
        if (redirect) {
          return { redirect };
        }
      }
    }
  },
});
